import Cookies from 'js-cookie';
import type { SubscriptionFlags } from '../types';

/**
 * Helper to check if a URL param value is truthy (non-null, non-undefined, non-empty).
 * Matches Vue's behavior: `value !== null && value !== undefined && value !== ''`
 */
function isParamTruthy(value: string | null): boolean {
    return value !== null && value !== undefined && value !== '';
}

/**
 * Gets subscription flags from cookies and optionally from URL search params.
 * When searchParams are provided, URL values take precedence and are saved to cookies.
 *
 * URL param names match Vue's App.vue trackTraffic() implementation:
 * - show_promo OR promo → show_promo cookie
 * - code → discount_code cookie
 * - annual → show_annual cookie
 * - monthly → show_monthly cookie
 * - page → page cookie
 * - subscription_tier → subscription_tier cookie
 * - subscription_annual → subscription_annual cookie
 * - trialDays → trial_days cookie
 */
export function getSubscriptionFlags(searchParams?: URLSearchParams): SubscriptionFlags {
    const flags: SubscriptionFlags = {
        page: (Cookies.get('page') as 'subscribev1' | 'subscribev2') || undefined,
        add_socials_before_subscription: Cookies.get('add_socials_before_subscription') === 'true',
        show_promo: Cookies.get('show_promo') === 'true',
        discount_code: Cookies.get('discount_code') || undefined,
        show_annual: Cookies.get('show_annual') === 'true',
        show_monthly: Cookies.get('show_monthly') === 'true',
        subscription_tier: (Cookies.get('subscription_tier') as 'creator' | 'creator-pro') || undefined,
        subscription_annual: Cookies.get('subscription_annual') === 'true',
        trial_days: parseInt(Cookies.get('trial_days') || '') || undefined,
    };

    if (searchParams) {
        // page
        const urlPage = searchParams.get('page');
        if (urlPage === 'subscribev1' || urlPage === 'subscribev2') {
            flags.page = urlPage;
            Cookies.set('page', urlPage, { expires: 1 });
        }

        // trialDays → trial_days cookie
        const urlTrialDays = searchParams.get('trialDays');
        if (urlTrialDays) {
            const parsed = parseInt(urlTrialDays);
            if (!isNaN(parsed)) {
                flags.trial_days = parsed;
                Cookies.set('trial_days', urlTrialDays, { expires: 7 });
            }
        }

        // show_promo OR promo → show_promo cookie (Vue behavior: any non-empty value is truthy)
        let showPromo = searchParams.get('show_promo');
        if (!isParamTruthy(showPromo)) {
            showPromo = searchParams.get('promo');
        }
        if (isParamTruthy(showPromo)) {
            flags.show_promo = true;
            Cookies.set('show_promo', 'true');
        }

        // code → discount_code cookie
        const urlCode = searchParams.get('code');
        if (urlCode) {
            flags.discount_code = urlCode;
            Cookies.set('discount_code', urlCode);
        }

        // annual → show_annual cookie (Vue behavior: any non-empty value is truthy)
        const urlAnnual = searchParams.get('annual');
        if (isParamTruthy(urlAnnual)) {
            flags.show_annual = true;
            Cookies.set('show_annual', 'true');
        }

        // monthly → show_monthly cookie (Vue behavior: any non-empty value is truthy)
        const urlMonthly = searchParams.get('monthly');
        if (isParamTruthy(urlMonthly)) {
            flags.show_monthly = true;
            Cookies.set('show_monthly', 'true');
        }

        // subscription_tier
        const urlSubscriptionTier = searchParams.get('subscription_tier');
        if (urlSubscriptionTier === 'creator' || urlSubscriptionTier === 'creator-pro') {
            flags.subscription_tier = urlSubscriptionTier;
            Cookies.set('subscription_tier', urlSubscriptionTier);
        }

        // subscription_annual
        const urlSubscriptionAnnual = searchParams.get('subscription_annual');
        if (urlSubscriptionAnnual === 'true' || urlSubscriptionAnnual === 'false') {
            flags.subscription_annual = urlSubscriptionAnnual === 'true';
            Cookies.set('subscription_annual', urlSubscriptionAnnual);
        }

        // add_socials_before_subscription
        const urlAddSocials = searchParams.get('add_socials_before_subscription');
        if (urlAddSocials === 'true' || urlAddSocials === 'false') {
            flags.add_socials_before_subscription = urlAddSocials === 'true';
            Cookies.set('add_socials_before_subscription', urlAddSocials);
        }
    }

    return flags;
}

/**
 * Saves subscription flags from URL params to cookies.
 * This should be called once on app load to persist flags for the subscription flow.
 * Mirrors Vue's App.vue trackTraffic() behavior.
 */
export function saveSubscriptionFlagsFromUrl(): void {
    const url = new URL(window.location.href);
    getSubscriptionFlags(url.searchParams);
}

export function getProgressPercentage(
    currentStep: 'socials' | 'plan' | 'payment',
    hasSocialsExperiment: boolean
): number {
    if (hasSocialsExperiment) {
        const percentages = { socials: 40, plan: 60, payment: 80 };
        return percentages[currentStep];
    }
    const percentages = { socials: 50, plan: 50, payment: 75 };
    return percentages[currentStep];
}
